import React from "react";

const References = () => {
  return (
    <React.Fragment>
      <h1>References</h1>

      <h2>
        <strong>Reference Limits for Free Publication Eligibility</strong>
      </h2>
      <p>
        Articles are limited to the following number of references to remain eligible for free
        publication. Exceeding the limits below will disqualify your article from free publication
        regardless of how many errors are found during our preliminary review. All review articles
        require the purchase of Preferred Editing regardless of the total number of cited
        references.
      </p>
      <div className="reference-limits row centered">
        <div className="small-6 medium-offset-2 medium-2 columns reference-limit-item">
          <div className="small-font blue">
            <strong>
              Original
              <br />
              Articles
            </strong>
          </div>
          <div className="large-font space-top-s">
            <strong>200</strong>
          </div>
          <div className="small-font space-top-xxs">
            <strong>References</strong>
          </div>
        </div>
        <div className="small-6 medium-2 columns reference-limit-item">
          <div className="small-font blue">
            <strong>
              Technical
              <br />
              Reports
            </strong>
          </div>
          <div className="large-font space-top-s">
            <strong>200</strong>
          </div>
          <div className="small-font space-top-xxs">
            <strong>References</strong>
          </div>
        </div>
        <div className="small-6 medium-2 columns reference-limit-item">
          <div className="small-font blue">
            <strong>
              Case
              <br />
              Study
            </strong>
          </div>
          <div className="large-font space-top-s">
            <strong>200</strong>
          </div>
          <div className="small-font space-top-xxs">
            <strong>References</strong>
          </div>
        </div>
        <div className="small-6 small-centered medium-2 medium-uncentered columns end reference-limit-item last">
          <div className="small-font blue pad-top-10">
            <strong>Letter</strong>
          </div>
          <div className="large-font pad-top-36">
            <strong>200</strong>
          </div>
          <div className="small-font space-top-xxs">
            <strong>References</strong>
          </div>
        </div>
        <div className="small-6 small-centered medium-2 medium-uncentered columns end reference-limit-item last">
          <div className="small-font blue pad-top-10">
            <strong>Editorials</strong>
          </div>
          <div className="large-font pad-top-36">
            <strong>200</strong>
          </div>
          <div className="small-font space-top-xxs">
            <strong>References</strong>
          </div>
        </div>
      </div>

      <h2>
        <strong>Digital Object Identifiers (DOIs) Required if Available </strong>
      </h2>
      <p>
        The DOI is a string of numbers that uniquely identifies a published article. The DOI is
        permanently assigned to an article and provides a persistent link to current information
        about that article, including location. This enables readers to find the online article
        irrespective of any subsequent changes in the website structure, management responsibility
        of the journal in which it was first published, or location of the website on which the
        journal is hosted.
      </p>
      <p>
        CJoE requires DOI numbers to be included in all references when available. If a DOI cannot
        be found, please add the article URL to the URL reference field instead.
      </p>

      <h2>
        <strong>Citing Unpublished Material</strong>
      </h2>
      <p>
        Content that has not yet been accepted for publication and does not have a DOI should be
        noted as “unpublished data” and should not be included in the reference list. Articles that
        are pending publication elsewhere may be cited. However, “(IN PRESS)” must be included at
        the end of the article title in the reference. Please note that only accepted articles
        qualify for the “IN PRESS” mention. Articles currently under review cannot be included in
        this category.
      </p>
      <p>
        All content included in the reference list must have been previously published in citable
        journals or books. If you wish to reference material that does not meet this specification,
        such as an abstract without DOI, poster, unpublished data, or personal communication, please
        include information about this source within the text of your article. If a poster or
        abstract has a DOI, it should be treated as published material and cited in the reference
        list.
      </p>
      <p>
        To mention an abstract (with no DOI), poster, or other unpublished material in the body of
        your article, please use the following format: (Type of unpublished material: Author names.
        Title in Title Case. Meeting Title (if applicable); Date)” If needed, repeated subsequent
        citations for the same item should be shortened to include first author name and date as
        follows: “(Type of unpublished material: First author name, date).
      </p>
      <p>
        Preprints should be included in the reference list and cited accordingly and “[PREPRINT]”
        should be included after the article title in the corresponding reference.
      </p>

      <h2>
        <strong>Reference Citations</strong>
      </h2>
      <p>
        References should be cited using square brackets. Reference citations may be placed
        mid-sentence or at the end of a sentence (but before the period). For example: This is an
        example sentence [1]. If multiple references are cited in one sentence, please list them as
        follows: [1-4], [1,3], [5,9-12], etc. Footnotes are not permitted.
      </p>
      <p>
        Please number references in the order in which they are mentioned in the text; they should
        not be alphabetized. All co-authors must be cited when creating a reference unless there are
        more than seven authors.{" "}
        <u>If more than seven authors, insert “et al.” after the first three authors.</u> Author
        names should be formatted as seen in the examples below.
      </p>

      <h2>
        <strong>Additional Formatting Requirements</strong>
      </h2>
      <p>
        Please adhere to the following requirements for each reference type. Submissions that do not
        follow these requirements will be required to purchase our{" "}
        <a href="/cureus_editing_services" target="_blank" rel="noreferrer">
          Preferred Editing Service
        </a>
        . Please pay particular attention to the following:
      </p>
      <ul className="indented-for-medium-up ref-formatting">
        <li className="warning space-bottom-xs">
          <strong>
            References missing required information (page numbers, title, authors, etc.) will always
            be the responsibility of the author even if Preferred Editing has been purchased.
          </strong>
        </li>
        <li className="warning space-bottom-xs">
          <strong>
            Do not link to a website that no longer exists. (Defunct journals may still be
            referenced.)
          </strong>
        </li>
        <li className="warning space-bottom-xs">
          <strong>
            When referencing a book (chapter or whole), editors (if available), publisher, and
            publisher location must be listed.
          </strong>
        </li>
        <li className="warning space-bottom-xs">
          <strong>
            When referencing a journal article, only the first letter of the first word should be
            capitalized. (Note: Book titles should be listed in title case, i.e. the first letter of
            each word is capitalized.)
          </strong>
        </li>
        <li className="warning space-bottom-xs">
          <strong>
            Website references must include an access date and the name of the citation (i.e. not
            just the URL).
          </strong>
        </li>
        <li className="warning space-bottom-xs">
          <strong>
            List only the volume number of the journal. Issue and supplement numbers should not be
            included. If referencing a book chapter, please include the edition and volume numbers.
          </strong>
        </li>
        <li className="warning space-bottom-xs">
          <strong>Please list the page numbers of the cited chapter or article.</strong>
        </li>
        <li className="warning space-bottom-xs">
          <strong>
            The DOI number should be added to the end of the reference if available. The “DOI”
            abbreviation should not be included.
          </strong>
        </li>
        <li className="warning">
          <strong>All references must include a DOI or URL unless neither exist.</strong>
        </li>
      </ul>
      <p>Prior to converting your references, please ensure they are formatted as follows:</p>
      <div className="callout-box">
        <h5>Article within a journal</h5>
        <p>
          Watson JEM, Dudley N, Segan DB, Hockings M:{" "}
          <a
            href="https://dx.doi.org/10.1038/nature13947?utm_medium=email&utm_source=transaction"
            target="_blank"
            rel="noreferrer noopener"
          >
            The performance and potential of protected areas
          </a>
          . Nature. 2014, 515:67-73.{" "}
          <a
            href="https://dx.doi.org/10.1038/nature13947?utm_medium=email&utm_source=transaction"
            target="_blank"
            rel="noreferrer noopener"
          >
            10.1038/nature13947
          </a>
        </p>
        <br></br>
        <p>
          Ambak R, Tupang L, Hasim MH, et al.:{" "}
          <a
            href="https://www.itmedicalteam.pl/articles/who-do-not-read-and-understand-food-label-in-malaysia-findings-from-a-population-study-106007.html?utm_medium=email&utm_source=transaction"
            target="_blank"
            rel="noreferrer noopener"
          >
            Who do not read and understand food label in Malaysia? Findings from a population study
          </a>
          . Health Science Journal. 2018, 12:1-7.
        </p>
      </div>
      <div className="callout-box">
        <h5>Website</h5>
        <p>
          Gentleman R, Ihaka R, Bates D, Chambers J, Dalgaard J: The R project for statistical
          computing. (2009).{" "}
          <a href="http://www.r-project.org/254" target="_blank" rel="noreferrer noopener">
            http://www.r-project.org/254
          </a>
          .
        </p>
        <p>
          Cureus Journals: Opening doors to boundless knowledge [video file]. (2024). Accessed:
          March 4, 2025.{" "}
          <a
            href="https://www.youtube.com/watch?v=uB3qFM3IOAk"
            target="_blank"
            rel="noreferrer noopener"
          >
            https://www.youtube.com/watch?v=uB3qFM3IOAk
          </a>
        </p>
      </div>
      <div className="callout-box">
        <h5>Complete book</h5>
        <p>
          Pyrgidis CN:{" "}
          <a
            href="https://dx.doi.org/10.1201/9781003046073?utm_medium=email&utm_source=transaction"
            target="_blank"
            rel="noreferrer noopener"
          >
            Railway Transportation Systems: Design, Construction and Operation
          </a>
          . CRC Press, Boca Raton; 2021.{" "}
          <a
            href="https://dx.doi.org/10.1201/9781003046073?utm_medium=email&utm_source=transaction"
            target="_blank"
            rel="noreferrer noopener"
          >
            10.1201/9781003046073
          </a>
        </p>
      </div>
      <div className="callout-box">
        <h5>Book chapter or article within a book</h5>
        <p>
          Canela MA, Alegre I, Ibarra A: Multiple regression. Quantitative Methods for Management.
          Springer, Cham; 2019. 37-45. 10.1007/978-3-030-17554-2
        </p>
      </div>
      <p>
        If you choose to use reference management software, Cureus JBE recommends{" "}
        <a href="https://endnote.com/" target="_blank" rel="noreferrer noopener">
          EndNote
        </a>{" "}
        for easy management and formatting of citations and reference lists.
      </p>
    </React.Fragment>
  );
};

export default References;
